import React from 'react';
import {
  Button,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { OverlayProps } from 'components/TaskOverlay';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import {
  SignersDetailsModal_node$data,
  SignersDetailsModal_node$key,
} from './__generated__/SignersDetailsModal_node.graphql';
import { UserHandle } from 'components/UserHandle';
import { useI18n } from 'utils/i18n';
import {
  SignersDetailsModal_createReminder_Mutation,
  SignersDetailsModal_createReminder_Mutation$data,
} from './__generated__/SignersDetailsModal_createReminder_Mutation.graphql';
import { PayloadError } from 'relay-runtime';

const nodeFragment = graphql`
  fragment SignersDetailsModal_node on Node {
    __typename
    id
    ... on SignableInterface {
      signatureEnvelope {
        id
        signatures {
          nodes {
            id
            status
            signedAt {
              datetime
            }
            member {
              id
              ...UserHandle_member
            }
          }
        }
      }
    }
  }
`;

const createReminderMutation = graphql`
  mutation SignersDetailsModal_createReminder_Mutation(
    $input: RemindersCreateMutationInput!
  ) {
    createReminder(input: $input) {
      success
    }
  }
`;

interface SignersDetailsModalProps extends OverlayProps {
  signableNode: SignersDetailsModal_node$key;
}

export const SignersDetailsModal = ({
  isOpen,
  onClose,
  signableNode,
}: SignersDetailsModalProps) => {
  const { id, signatureEnvelope } = useFragment(nodeFragment, signableNode);
  const bg = useColorModeValue('gray.100', 'gray.800');
  const { format } = useI18n();
  const toast = useToast();

  const [createReminder, isCreateReminderLoading] =
    useMutation<SignersDetailsModal_createReminder_Mutation>(
      createReminderMutation,
    );

  const signedSignatures = signatureEnvelope?.signatures.nodes.filter(
    (signature) => signature.status === 'SIGNED',
  );
  const pendingSignatures = signatureEnvelope?.signatures.nodes.filter(
    (signature) => signature.status === 'WAITING',
  );

  const remindAll = () => {
    const signeeIds = pendingSignatures?.length
      ? pendingSignatures.map((pendingSignature) => pendingSignature.member.id)
      : null;

    if (!signeeIds) {
      throw new Error(t`No signees to remind`);
    }

    createReminder({
      variables: {
        input: {
          memberIds: signeeIds,
          nodeId: id,
          message: '',
        },
      },
      onCompleted,
      onError,
    });
  };

  const onCompleted = (
    response: SignersDetailsModal_createReminder_Mutation$data,
    errors: PayloadError[] | null,
  ) => {
    if (errors) {
      onError();
    } else {
      toast({
        status: 'success',
        title: t`Reminder sent successfully`,
      });
    }
  };

  const onError = () => {
    toast({
      status: 'error',
      title: t`Unable to send the reminder`,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t`Details`}</ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <Stack spacing={6}>
            <Stack>
              <Heading size="sm">{t`Signed`}</Heading>
              <Stack p={4} borderRadius="lg" bg={bg}>
                {signedSignatures?.length ? (
                  signedSignatures.map((signedSignature) => (
                    <UserHandle
                      key={signedSignature.id}
                      member={signedSignature.member}
                      showAvatar
                      avatarProps={{ size: 'sm' }}
                      nameProps={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        spacing: 0,
                      }}
                      linkToProfile
                    >
                      {signedSignature.signedAt?.datetime ? (
                        <Text fontSize="sm">
                          {format(
                            new Date(signedSignature.signedAt.datetime),
                            'PPPp',
                          )}
                        </Text>
                      ) : null}
                    </UserHandle>
                  ))
                ) : (
                  <Text fontStyle="italic">{t`No signatures yet`}</Text>
                )}
              </Stack>
            </Stack>
            <Stack>
              <HStack justifyContent="space-between">
                <Heading size="sm">{t`Waiting to Sign`}</Heading>
                <Button
                  size="xs"
                  colorScheme="blue"
                  onClick={remindAll}
                  isLoading={isCreateReminderLoading}
                  isDisabled={isCreateReminderLoading}
                >{t`Remind All`}</Button>
              </HStack>
              <Stack p={4} borderRadius="lg" bg={bg}>
                {pendingSignatures?.length ? (
                  pendingSignatures.map((pendingSignature) => (
                    <PendingSignature
                      key={pendingSignature.id}
                      nodeId={id}
                      pendingSignature={pendingSignature}
                      onCompleted={onCompleted}
                      onError={onError}
                    />
                  ))
                ) : (
                  <Text fontStyle="italic">{t`All parties have signed`}</Text>
                )}
              </Stack>
            </Stack>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

interface PendingSignatureProps {
  nodeId: string;
  pendingSignature: NonNullable<
    SignersDetailsModal_node$data['signatureEnvelope']
  >['signatures']['nodes'][0];
  onCompleted: (
    response: SignersDetailsModal_createReminder_Mutation$data,
    errors: PayloadError[] | null,
  ) => void;
  onError: () => void;
}

const PendingSignature = ({
  nodeId,
  pendingSignature,
  onCompleted,
  onError,
}: PendingSignatureProps) => {
  const [createReminder, isCreateReminderLoading] =
    useMutation<SignersDetailsModal_createReminder_Mutation>(
      createReminderMutation,
    );

  const remindSignee = () => {
    const signeeId = pendingSignature.member.id;

    createReminder({
      variables: {
        input: {
          memberIds: [signeeId],
          nodeId,
          message: '',
        },
      },
      onCompleted,
      onError,
    });
  };

  return (
    <HStack justifyContent="space-between">
      <UserHandle
        member={pendingSignature.member}
        showAvatar
        avatarProps={{ size: 'sm' }}
        linkToProfile
      />
      <Button
        size="xs"
        colorScheme="blue"
        onClick={remindSignee}
        isLoading={isCreateReminderLoading}
        isDisabled={isCreateReminderLoading}
      >{t`Send Reminder`}</Button>
    </HStack>
  );
};
